// createNewNoteForm.component.jsx

import React, { useEffect, useState } from "react";
import { getUserFromToken } from "../../../../utils/functions.ts";
import FBXCalendar from "../../../Universals/FBXCalendar/fbxcalendar.component.jsx";
import ClearInputBox from "../../../Universals/newInputBox/newInputBox.component.jsx";

const CreateNewNoteForm = ({
  activePlayer,
  newNote,
  onChange,
  onTogglePrivacy,
  onToday,
  onCancel,
  onSave, // optional: if provided, we call this with the built payload; otherwise console.log only
}) => {
  const fullName = activePlayer
    ? `${(activePlayer.last || "").toUpperCase()}, ${(activePlayer.first || "").toUpperCase()}`
    : "NEW NOTE";

  // match createReport behavior (initialize defaults)
  useEffect(() => {
    onToday?.();
    onChange?.("SourceData", "");
    // DateOfContact is set by onToday (parent), but keep it safe:
    // onChange?.("DateOfContact", "");
    onTogglePrivacy?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [openCalendar, setOpenCalendar] = useState(null);
  const handleToggleCalendar = (fieldName) => {
    setOpenCalendar((prev) => (prev === fieldName ? null : fieldName));
  };

  const canCreate = Boolean(newNote?.DateOfContact && (newNote?.SourceData ?? "").trim());

  const onCreate = () => {
    if (!activePlayer?.FBXID) {
      alert("No player selected.");
      return;
    }
    if (!canCreate) {
      alert("Please select a date and enter a title.");
      return;
    }

    // Parent (NoteBase) owns the backend call
    onSave?.();
  };


  return (
    <div className="container p-0 mb-5">
      <div className="row justify-content-center">
        <div className="col-48 bg-white rounded shadow-sm p-0 border border-1 border-navy">
          {/* Header bar (same look as createReport) */}
          <div
            className="d-flex gap-2 align-items-center mb-2 alert-secondary ps-3"
            style={{ lineHeight: "45px", height: "45px" }}
          >
            You are creating a new note for:
            <div className="fw-bold text-primary">{fullName}</div>
            <div className="fw-bold text-muted">{activePlayer?.colPos1 || "POS"}</div>
            <div className="fw-bold text-muted">[{activePlayer?.school || "SCHOOL"}]</div>
            <div className="fw-bold text-muted">
              College # {activePlayer?.jersey === "-1" ? "0" : activePlayer?.jersey}
            </div>
          </div>

          <div className="p-3">
            {/* Note Date row */}
            <div className="row g-2 align-items-center mb-2">
              <div className="col-8 text-end fw-semibold">Note Date:</div>
              <div className="col-10">
                <FBXCalendar
                  fieldName="DateOfContact"
                  dateIn={newNote?.DateOfContact}
                  minYear="1990"
                  maxYear="2025"
                  style={{ width: "100%" }}
                  placeholder="Note Date"
                  onDateChange={(value) => onChange?.("DateOfContact", value)}
                  onToggleCalendar={handleToggleCalendar}
                  isOpen={openCalendar === "DateOfContact"}
                />
              </div>
              <div className="text-danger text-start fs-8 col-12">*Required</div>
            </div>

            {/* Title row */}
            <div className="row g-2 align-items-center mb-2">
              <div className="col-8 text-end fw-semibold">Title:</div>
              <div className="col-10">
                <ClearInputBox
                  value={newNote?.SourceData ?? ""}
                  placeholder="Add a title…"
                  onChange={(e) => onChange?.("SourceData", e.target.value)}
                />
              </div>
              <div className="text-danger text-start fs-8 col-12">*Required</div>
            </div>

            {/* Privacy row (same structure as createReport) */}
            <div className="row g-2 align-items-center mb-2">
              <div className="col-8 text-end fw-semibold">Privacy:</div>

              <div className="col-10">
                <input
                  type="text"
                  readOnly
                  className={`form-control form-control-sm ${newNote?.isPrivate ? "bg-purple text-white" : ""}`}
                  value={newNote?.isPrivate ? "This note will be Private" : "Currently: Not Private"}
                />
              </div>

              <div className="col-24 col-md-18 d-flex gap-2">
                <button
                  type="button"
                  className={`btn btn-sm w-50 ${newNote?.isPrivate ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => onTogglePrivacy?.(true)}
                >
                  Private
                </button>
                <button
                  type="button"
                  className={`btn btn-sm w-50 ${!newNote?.isPrivate ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => onTogglePrivacy?.(false)}
                >
                  Not Private
                </button>
              </div>
            </div>

            {/* Create / Cancel row (same look as createReport) */}
            <div className="row g-2 align-items-center mb-2">
              <div className="col-8 text-end fw-semibold">Create:</div>
              <div className="col-10">
                <button type="button" className="btn btn-outline-danger w-100" onClick={onCancel}>
                  Cancel
                </button>
              </div>

              <div className="col-24 col-md-18">
                {canCreate ? (
                  <button type="button" className="btn btn-outline-success w-100" onClick={onCreate}>
                    Create
                  </button>
                ) : (
                  <button type="button" className="btn btn-outline-secondary w-100" disabled>
                    Select Date &amp; Title to Create
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateNewNoteForm;
